/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { StreamingLiveCommerceAddressV2 } from "./StreamingLiveCommerceAddressV2";
import { StreamingLiveCommercePaymentMethodV2 } from "./StreamingLiveCommercePaymentMethodV2";
import { StreamingLiveCommerceAmountV2 } from "./StreamingLiveCommerceAmountV2";
import { useI18n } from "hooks/i18n/i18n";
import React, { useEffect, useRef } from "react";
import { useSelector } from "store";
import { TextLink } from "ui/TextLink";
import { scrollTop } from "utility/index";

import type { ProductData } from "@spwn/types/firebase/firestore";
import type { Store } from "store";
import type { AddressInfo } from "modules/auth";
import { StreamingLiveCommerceCartLayoutV2 } from "./StreamingLiveCommerceCartLayoutV2";

type Props = {
  THEOPlayerElementHeight: number;
  pseudoCart: (ProductData & { count: number })[];
  addressInfo: AddressInfo | null;
  shippingFee: number;
  cartTotalFee: number;
  totalFee: number;
  settlementFee: number;
  isCalculatingShippingFee: boolean;
  isError: boolean;
  incrementCartItem: (itemData: ProductData) => void;
  decrementCartItem: (itemData: ProductData) => void;
  removeCartItem: (itemData: ProductData) => void;
  closeLiveCommerceCart: () => void;
  proceedToCheckout: () => void;
};
const StreamingLiveCommerceCartV2: React.FC<Props> = (props) => {
  // PawCharge component supposed to call fetchCardInfo to fetch cardInfoList
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );

  const { t } = useI18n();
  const liveCommerceRef = useRef<HTMLDivElement>(null);
  const classesLiveCommerceCartDesign =
    liveCommerceCartDesign(isDarkModeEnabled);

  const didMount = () => {
    scrollTop();
  };
  useEffect(didMount, []);

  const addressSection = (
    <StreamingLiveCommerceAddressV2 addressInfo={props.addressInfo} />
  );

  const paymentSection = (
    <StreamingLiveCommercePaymentMethodV2 liveCommerceRef={liveCommerceRef} />
  );

  const summarySection = (
    <StreamingLiveCommerceAmountV2
      isDarkModeEnabled={isDarkModeEnabled}
      cartTotalFee={props.cartTotalFee}
      shippingFee={props.shippingFee}
      settlementFee={props.settlementFee}
      totalFee={props.totalFee}
      isCalculatingShippingFee={props.isCalculatingShippingFee}
    />
  );

  const footerSection = props.isError ? (
    <div css={classesLiveCommerceCartDesign.button}>
      <p>{t("streaming.commerceCart.noticeInvalidAddress")}</p>
      <TextLink href={`https://${window.location.hostname}/account/address`}>
        {t("streaming.commerceCart.editAddress")}
      </TextLink>
    </div>
  ) : null;

  return (
    <StreamingLiveCommerceCartLayoutV2
      isDarkMode={isDarkModeEnabled}
      playerHeight={props.THEOPlayerElementHeight}
      liveCommerceRef={liveCommerceRef}
      addressSection={addressSection}
      paymentSection={paymentSection}
      summarySection={summarySection}
      footerSection={footerSection}
      handleBackButton={props.closeLiveCommerceCart}
    />
  );
};

const liveCommerceCartDesign = (isDarkModeEnabled: boolean) => {
  const button = css`
    padding: 12px 0;
    p {
      color: ${isDarkModeEnabled ? "#fff" : "#000"};
      font-size: 14px;
      line-height: 1.5em;
      margin-bottom: 8px;
    }
    a {
      display: block;
      width: 100%;
      padding: 12px 0;
      color: #fff;
      font-size: 14px;
      text-align: center;
      text-decoration: none;
      background-color: #00c2ae;
      border: none;
      outline: none;
      appearance: none;
      cursor: pointer;
      &.disabled {
        background-color: #b1b1b1;
      }
    }
  `;
  return {
    button,
  };
};

export default StreamingLiveCommerceCartV2;
